/**
 * FieldCustomizer — shared component that lets users:
 *   1. Rename stat card labels
 *   2. Choose which API field maps to each card
 *   3. Show/hide table columns
 *   4. Rename table column headers
 *   5. Choose which API field maps to each column
 *
 * Usage:
 *   <FieldCustomizer
 *     storageKey="dashboard_userpanel"       // unique key per widget per page
 *     cards={[                               // optional: for stat cards
 *       { id:"running_hours", label:"Running Hours", field:"total_running_hours", icon:"ri-time-line", color:"text-primary" },
 *       ...
 *     ]}
 *     columns={[                             // optional: for tables
 *       { id:"date",    label:"Date",    field:"date"    },
 *       { id:"station", label:"Station", field:"station" },
 *       ...
 *     ]}
 *     availableFields={[                     // all API fields available to choose from
 *       { value:"total_running_hours", label:"Total Running Hours" },
 *       { value:"good_parts",          label:"Good Parts" },
 *       ...
 *     ]}
 *     onCardsChange={(cards) => ...}         // callback when card config changes
 *     onColumnsChange={(cols) => ...}        // callback when column config changes
 *   />
 */

import React, { useState, useEffect } from "react";
import "./FieldCustomizer.css";

/* ── storage helpers ─────────────────────────────────────────────── */
const save = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };
const load = (key, def) => { try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : def; } catch { return def; } };

/* ── icons for common field types ───────────────────────────────── */
const FIELD_ICONS = {
  good_parts: "ri-checkbox-circle-line",
  bad_parts: "ri-close-circle-line",
  total_parts: "ri-group-line",
  total_running_hours: "ri-time-line",
  stopped_time: "ri-pause-circle-line",
  machine_running_hours: "ri-time-line",
};

/* ════════════════════════════════════════════════════════════════════
   useFieldCustomizer — hook to manage card + column config with persist
════════════════════════════════════════════════════════════════════ */
export const useFieldCustomizer = (storageKey, defaultCards = [], defaultColumns = []) => {
  const companyId = localStorage.getItem("companyid") || 1;
const cardKey = `fc_cards_${companyId}_${storageKey}`;
const colKey  = `fc_cols_${companyId}_${storageKey}`;
  // const cardKey = `fc_cards_${storageKey}`;
  // const colKey  = `fc_cols_${storageKey}`;

  const [cards,   setCards]   = useState(() => load(cardKey,   defaultCards));
  const [columns, setColumns] = useState(() => load(colKey,    defaultColumns));

  const updateCards = (next) => { setCards(next);   save(cardKey, next); };
  const updateCols  = (next) => { setColumns(next); save(colKey,  next); };

  // Reset to defaults
  const resetFields = () => {
    setCards(defaultCards);   save(cardKey, defaultCards);
    setColumns(defaultColumns); save(colKey, defaultColumns);
  };

  return { cards, columns, updateCards, updateCols, resetFields };
};

/* ════════════════════════════════════════════════════════════════════
   StatCards — renders stat cards from config
════════════════════════════════════════════════════════════════════ */
export const StatCards = ({ cards, data, colorMap = {} }) => {
  const visible = cards.filter(c => c.visible !== false);
  return (
    <div className="fc-stat-row">
      {visible.map((card) => {
        const value = data?.[card.field];
        const icon  = card.icon  || FIELD_ICONS[card.field] || "ri-bar-chart-line";
        const color = card.color || colorMap[card.field]    || "text-primary";
        return (
          <div key={card.id} className="fc-stat-card">
            <div className="fc-stat-icon-wrap">
              <div className={`fc-stat-icon ${color}`}>
                <i className={icon} />
              </div>
            </div>
            <div className="fc-stat-info">
              <p className="fc-stat-label">{card.label}</p>
              <h5 className="fc-stat-value">{value ?? 0}</h5>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   DataTable — renders a table from column config + data rows
════════════════════════════════════════════════════════════════════ */
export const DataTable = ({ columns, rows = [], onRowClick }) => {
  const visible = columns.filter(c => c.visible !== false);
  if (!visible.length) return <p className="text-muted p-3">No columns selected.</p>;
  return (
    <div className="table-responsive fc-table-wrap">
      <table className="table table-hover table-bordered mb-0" style={{ fontSize: 13 }}>
        <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
          <tr>
            {visible.map(col => <th key={col.id}>{col.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={visible.length} className="text-center text-muted py-4">No data</td></tr>
          ) : rows.map((row, i) => (
            <tr key={i} style={onRowClick ? { cursor: "pointer" } : {}} onClick={() => onRowClick?.(row)}>
              {visible.map(col => (
                <td key={col.id}>{row[col.field] ?? "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════
   FieldCustomizerPanel — the settings UI shown inside Widget panel
════════════════════════════════════════════════════════════════════ */
const FieldCustomizerPanel = ({
  cards = [],
  columns = [],
  availableFields = [],
  onCardsChange,
  onColumnsChange,
  onReset,
}) => {
  const [tab, setTab] = useState(cards.length ? "cards" : "columns");
  const [editingCard, setEditingCard] = useState(null);
  const [editingCol,  setEditingCol]  = useState(null);

  /* ── Card handlers ──────────────────────────────────────────── */
  const updateCard = (id, patch) => {
    onCardsChange(cards.map(c => c.id === id ? { ...c, ...patch } : c));
  };
  const toggleCardVisible = (id) => updateCard(id, { visible: !(cards.find(c=>c.id===id)?.visible ?? true) });

  /* ── Column handlers ────────────────────────────────────────── */
  const updateCol = (id, patch) => {
    onColumnsChange(columns.map(c => c.id === id ? { ...c, ...patch } : c));
  };
  const toggleColVisible = (id) => updateCol(id, { visible: !(columns.find(c=>c.id===id)?.visible ?? true) });
  const moveCol = (id, dir) => {
    const idx = columns.findIndex(c => c.id === id);
    const next = [...columns];
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    onColumnsChange(next);
  };

  const hasBoth = cards.length > 0 && columns.length > 0;

  return (
    <div className="fc-panel">
      {/* tab switcher */}
      {hasBoth && (
        <div className="fc-tabs">
          <button className={`fc-tab${tab==="cards"?" active":""}`} onClick={()=>setTab("cards")}>
            <i className="mdi mdi-card-outline me-1"/>Cards
          </button>
          <button className={`fc-tab${tab==="columns"?" active":""}`} onClick={()=>setTab("columns")}>
            <i className="mdi mdi-table-column me-1"/>Columns
          </button>
        </div>
      )}

      {/* ── Cards tab ───────────────────────────────────────── */}
      {(tab === "cards" || !hasBoth) && cards.length > 0 && (
        <div>
          <p className="fc-section-hint">Rename cards or change which API field they show.</p>
          {cards.map(card => {
            const isEditing = editingCard === card.id;
            const isVisible = card.visible !== false;
            return (
              <div key={card.id} className={`fc-row${isVisible ? "" : " fc-row--hidden"}`}>
                <div className="fc-row-left">
                  <button className="fc-vis-btn" onClick={() => toggleCardVisible(card.id)} title={isVisible?"Hide":"Show"}>
                    <i className={`mdi ${isVisible ? "mdi-eye" : "mdi-eye-off"}`} />
                  </button>
                  <div className="fc-row-info">
                    <span className="fc-row-label">{card.label}</span>
                    <span className="fc-row-field">→ {card.field}</span>
                  </div>
                </div>
                <button className="fc-edit-btn" onClick={() => setEditingCard(isEditing ? null : card.id)}>
                  <i className={`mdi ${isEditing ? "mdi-check" : "mdi-pencil"}`} />
                </button>

                {isEditing && (
                  <div className="fc-edit-form">
                    <label className="fc-edit-label">Display Name</label>
                    <input
                      className="form-control form-control-sm mb-2"
                      value={card.label}
                      onChange={e => updateCard(card.id, { label: e.target.value })}
                      placeholder="Card label..."
                    />
                    <label className="fc-edit-label">API Field</label>
                    <select
                      className="form-select form-select-sm mb-2"
                      value={card.field}
                      onChange={e => updateCard(card.id, { field: e.target.value })}
                    >
                      {availableFields.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                    <label className="fc-edit-label">Icon Class <small className="text-muted">(Remix / MDI)</small></label>
                    <input
                      className="form-control form-control-sm mb-2"
                      value={card.icon || ""}
                      onChange={e => updateCard(card.id, { icon: e.target.value })}
                      placeholder="e.g. ri-time-line"
                    />
                    <label className="fc-edit-label">Color Class</label>
                    <select className="form-select form-select-sm" value={card.color||"text-primary"}
                      onChange={e => updateCard(card.id, { color: e.target.value })}>
                      {["text-primary","text-success","text-danger","text-warning","text-info","text-secondary"].map(c=>(
                        <option key={c} value={c}>{c.replace("text-","")}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Columns tab ─────────────────────────────────────── */}
      {(tab === "columns" || !hasBoth) && columns.length > 0 && (
        <div>
          <p className="fc-section-hint">Show/hide columns, rename headers, or change the API field.</p>
          {columns.map((col, idx) => {
            const isEditing = editingCol === col.id;
            const isVisible = col.visible !== false;
            return (
              <div key={col.id} className={`fc-row${isVisible ? "" : " fc-row--hidden"}`}>
                <div className="fc-row-left">
                  <button className="fc-vis-btn" onClick={() => toggleColVisible(col.id)} title={isVisible?"Hide":"Show"}>
                    <i className={`mdi ${isVisible ? "mdi-eye" : "mdi-eye-off"}`} />
                  </button>
                  <div className="fc-move-btns">
                    <button className="fc-move-btn" disabled={idx===0} onClick={()=>moveCol(col.id,"up")}>▲</button>
                    <button className="fc-move-btn" disabled={idx===columns.length-1} onClick={()=>moveCol(col.id,"down")}>▼</button>
                  </div>
                  <div className="fc-row-info">
                    <span className="fc-row-label">{col.label}</span>
                    <span className="fc-row-field">→ {col.field}</span>
                  </div>
                </div>
                <button className="fc-edit-btn" onClick={() => setEditingCol(isEditing ? null : col.id)}>
                  <i className={`mdi ${isEditing ? "mdi-check" : "mdi-pencil"}`} />
                </button>

                {isEditing && (
                  <div className="fc-edit-form">
                    <label className="fc-edit-label">Column Header</label>
                    <input
                      className="form-control form-control-sm mb-2"
                      value={col.label}
                      onChange={e => updateCol(col.id, { label: e.target.value })}
                      placeholder="Column header..."
                    />
                    <label className="fc-edit-label">API Field</label>
                    <select
                      className="form-select form-select-sm"
                      value={col.field}
                      onChange={e => updateCol(col.id, { field: e.target.value })}
                    >
                      {availableFields.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {onReset && (
        <div className="text-center mt-3">
          <button className="btn btn-outline-danger btn-sm" onClick={onReset}>
            <i className="mdi mdi-refresh me-1"/>Reset to defaults
          </button>
        </div>
      )}
    </div>
  );
};

export default FieldCustomizerPanel;
